export interface OTAChannel {
  id: string;
  name: string;
  displayName: string;
  logo?: string;
  color: string;
}

export const OTA_CHANNELS: OTAChannel[] = [
  { id: "booking", name: "booking", displayName: "Booking.com", color: "bg-blue-600" },
  { id: "makemytrip", name: "Make My Trip", displayName: "MakeMyTrip", color: "bg-yellow-600" },
  // { id: "hotels", name: "hotels", displayName: "Hotels.com", color: "bg-red-600" },
  // { id: "airbnb", name: "airbnb", displayName: "Airbnb", color: "bg-pink-600" },
  // { id: "agoda", name: "agoda", displayName: "Agoda", color: "bg-purple-600" },
  // { id: "tripadvisor", name: "tripadvisor", displayName: "TripAdvisor", color: "bg-green-600" },
];

export interface Competitor {
  id: string;
  name: string;
  location?: string;
  distance?: number;
  distanceUnit?: "km" | "mi";
  starRating?: number;
  category?: "budget" | "mid-range" | "luxury";
  rooms?: number;
  amenities?: string[];
  is_active: boolean;
  trackedOTAs: string[]; // Array of OTA channel IDs to track
  branch_id?: number; // Branch ID to associate this competitor with
  createdAt: Date;
  updatedAt: Date;
}

export interface CompetitorPrice {
  id: string;
  competitorId: string;
  otaChannelId: string;
  date: string;
  roomType: string;
  price: number;
  availability: "available" | "limited" | "sold_out";
  lastUpdated: Date;
  currency: string;
}

export interface PriceSuggestion {
  date: string;
  currentPrice: number;
  suggestedPrice: number;
  change: number;
  changePercent: number;
  reasoning: string[];
  confidence: "low" | "medium" | "high";
  competitorAverage: number;
  occupancy: number;
  marketPosition: "below" | "at" | "above";
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface CompetitorAnalysis {
  dateRange: DateRange;
  competitors: Competitor[];
  prices: CompetitorPrice[];
  suggestions: PriceSuggestion[];
  summary: {
    averageMarketPrice: number;
    lowestPrice: number;
    highestPrice: number;
    yourCurrentPrice: number;
    positionInMarket: number;
    recommendedAction: "increase" | "decrease" | "maintain";
  };
}

// Daily competitor OTA prices interface for API
export interface CompetitorOTAPrices {
  competitorId: string;
  competitorName: string;
  bookingCom: number;
  expedia: number;
  hotelsCom: number;
  airbnb: number;
  agoda: number;
  tripadvisor: number;
  average: number;
}

export interface DailyCompetitorData {
  date: string;
  dayOfWeek: string;
  competitors: CompetitorOTAPrices[];
  marketAverage: number;
  occupancyRate: number;
}

export interface CompetitorPricesResponse {
  success: boolean;
  data: DailyCompetitorData[];
  dateRange: {
    start: string;
    end: string;
  };
  roomType?: string;
  roomVariant?: string;
}

export type SuggestionStatus = 'pending' | 'accepted' | 'rejected';

export type MarketStatus = 'underpriced' | 'overpriced' | 'aligned';

// Raw competitor data from JSON
export interface CompetitorRawData {
  property_name: string;
  platform: string;
  location: string;
  city: string;
  country: string;
  zip_code: string;
  checkin: string;
  checkout: string;
  scraped_date: string;
  room_type_name: string;
  room_description: string;
  room_size_sqrm: string;
  bed_type: string;
  total_number_of_beds: string;
  room_capacity_adults: string;
  room_capacity_children: string;
  room_view: string;
  price_per_night: string;
  original_price: string;
  discounted_price: string;
  tax_and_fees: string;
  total_with_tax: string;
  currency: string;
  promotion_applied: string;
  meal_plan: string;
}

export interface PricingAnalysisRow {
  date: string;
  dayOfWeek: string;
  yourPrice: number;
  marketAvg: number;
  marketAvgDiff: number;
  suggested: number;
  suggestedDiff: number;
  occupancy: number;
  suggestedPrice: number;
  change: number;
  changePercent: number;
  strategy: 'maintain' | 'increase' | 'decrease';
  confidence: 'low' | 'medium' | 'high';
  competitorPrices?: Record<string, number>;
  competitors: CompetitorRawData[];
  channelPrices: {
    bookingCom: number;
    expedia: number;
    hotelsCom: number;
    airbnb: number;
    agoda: number;
    tripadvisor: number;
  };
  channelDiff: {
    bookingCom: number;
    expedia: number;
    hotelsCom: number;
    airbnb: number;
    agoda: number;
    tripadvisor: number;
  };
  suggestionStatus: SuggestionStatus;
  marketStatus: MarketStatus;
  priceDifferencePercent: number;
  aiReasoning: string;
}

export interface OTABreakdown {
  channel: string;
  channelColor: string;
  price: number;
  vsYourPrice: number;
  vsYourPricePercent: number;
  status: 'lower' | 'higher' | 'same';
}

export const OTA_CHANNEL_COLORS: Record<string, string> = {
  'booking.com': '#003580',
  'booking': '#003580',
  'makemytrip': '#F7B500',
  'hotels.com': '#D32F2F',
  'hotels': '#D32F2F',
  'airbnb': '#FF5A5F',
  'agoda': '#7B2EA0',
  'tripadvisor': '#34E0A1',
};

// Smart pricing algorithm configuration
export interface PricingStrategy {
  minMarginPercent: number;
  aggressiveness: 'conservative' | 'moderate' | 'aggressive';
  marketPosition: 'budget' | 'mid-range' | 'luxury';
  occupancyThresholds: {
    low: number;
    high: number;
  };
}

export const DEFAULT_PRICING_STRATEGY: PricingStrategy = {
  minMarginPercent: 15,
  aggressiveness: 'moderate',
  marketPosition: 'mid-range',
  occupancyThresholds: {
    low: 50,
    high: 80,
  },
};
